import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import {
  pushEvent,
  ANALYTICS_EVENTS,
  generateLeadTransactionId,
  identifyUserFromLeadCapture,
  trackGoogleAdsLeadConversion,
} from '../../utils/analytics';
import { getAffiliateCodeForApi, getUtmParamsForApi } from '../../utils/utmTracker';
import { convertLeadToTrial } from '../../utils/stripeConversion';
import { confirmSetupWithClient, confirmPaymentWithClient } from '../../utils/stripeClient';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import { completeStripeAutoLogin } from '../../utils/stripeAutoLogin';
import StripeErrorBoundary from '../StripeErrorBoundary';
import { SparkleButton } from '../SparkleEffect';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import fetchWithTimeout from '../../utils/fetchWithTimeout';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import { classifyCheckoutResponse } from '../../utils/checkoutResponse';
import {
  CheckoutContactFields,
  CheckoutTrialCallout,
  CheckoutPaymentContainer,
  CheckoutCTAButton,
  HowFoundUsDropdown,
} from '../CheckoutFormShared';
import {
  SmsConsentCheckbox,
  buildSmsConsentPayload,
} from '../SmsConsent';
import './InlineCheckoutForm.css';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
const PAYMENT_INTENT_FETCH_TIMEOUT_MS = 15000;
const AUTO_LOGIN_FETCH_TIMEOUT_MS = 10000;

const TERMS_VERSION = new Date().toISOString().split('T')[0]; // Current date in YYYY-MM-DD format
const TERMS_DOC_TYPE = 'main_tos';
const SIGNUP_FRAMING_FEATURE_FLAGS = {
  sales_page_signup_framing_v4_variant: {
    id: 738187,
    version: 4,
    reason: 'Matched condition set 1',
  },
  sales_page_signup_framing_v5_variant: {
    id: 784226,
    version: 2,
    reason: 'Matched condition set 1',
  },
};

const getSignupFramingFeatureFlagKey = (experimentContext = {}) => {
  if (experimentContext.feature_flag && SIGNUP_FRAMING_FEATURE_FLAGS[experimentContext.feature_flag]) {
    return experimentContext.feature_flag;
  }

  return Object.keys(SIGNUP_FRAMING_FEATURE_FLAGS).find((flagKey) => (
    experimentContext[`$feature_flag_${flagKey}`]
  )) || 'sales_page_signup_framing_v4_variant';
};

const normalizeSignupFramingFeatureFlagVariant = (featureFlagKey, variant) => {
  if (!variant) {
    return variant;
  }

  if (
    (featureFlagKey === 'sales_page_signup_framing_v4_variant' && variant === 'full_form')
    || (featureFlagKey === 'sales_page_signup_framing_v5_variant' && variant === 'email_only')
  ) {
    return 'control';
  }
  if (
    ['sales_page_signup_framing_v4_variant', 'sales_page_signup_framing_v5_variant'].includes(featureFlagKey)
    && variant === 'login_menu'
  ) {
    return 'auth_first';
  }

  return variant;
};

const blurActiveFormControl = () => {
  const activeElement = document.activeElement;
  if (
    activeElement &&
    typeof activeElement.blur === 'function' &&
    ['INPUT', 'SELECT', 'TEXTAREA'].includes(activeElement.tagName)
  ) {
    activeElement.blur();
  }
};

const replaySignupFramingExposure = ({ exposureContext, parentExperimentContext, utmData }) => {
  const featureFlagKey = getSignupFramingFeatureFlagKey(exposureContext);
  const featureFlagContextProperty = `$feature_flag_${featureFlagKey}`;
  const featureFlagDefaults = SIGNUP_FRAMING_FEATURE_FLAGS[featureFlagKey];
  const rawFeatureFlagVariant = exposureContext?.[featureFlagContextProperty]
    || exposureContext?.[`$feature/${featureFlagKey}`]
    || exposureContext?.[featureFlagKey]
    || exposureContext?.sales_page_signup_framing_v5_variant
    || exposureContext?.sales_page_signup_framing_v4_variant
    || exposureContext?.experiment_variant
    || exposureContext?.variant
    || exposureContext?.sales_page_signup_framing_variant;
  const featureFlagVariant = normalizeSignupFramingFeatureFlagVariant(featureFlagKey, rawFeatureFlagVariant);
  const routeVariant = exposureContext?.sales_page_signup_framing_v5_route_variant
    || exposureContext?.sales_page_signup_framing_v4_route_variant
    || exposureContext?.sales_page_signup_framing_variant
    || exposureContext?.experiment_variant
    || exposureContext?.variant
    || featureFlagVariant;

  if (
    !featureFlagVariant
    || !exposureContext?.experiment_name
    || !featureFlagDefaults
  ) {
    return;
  }

  const posthogProps = window.posthog?.persistence?.props || {};
  const flagDetails = posthogProps.$feature_flag_details?.[featureFlagKey] || {};
  const flagMetadata = flagDetails.metadata || {};
  const flagReason = flagDetails.reason || {};
  const featureFlagId = flagMetadata.id || featureFlagDefaults.id;
  const featureFlagVersion = flagMetadata.version || featureFlagDefaults.version;
  const featureFlagReason = flagReason.description || featureFlagDefaults.reason;
  const enabledFeatureFlags = {
    ...(posthogProps.$enabled_feature_flags || {}),
    [featureFlagKey]: featureFlagVariant,
  };
  const activeFeatureFlags = Array.isArray(posthogProps.$active_feature_flags)
    ? [...new Set([...posthogProps.$active_feature_flags, featureFlagKey])]
    : [featureFlagKey];
  const replayContext = {
    ...parentExperimentContext,
    ...exposureContext,
    experiment_name: exposureContext.experiment_name,
    experiment_variant: routeVariant,
    feature_flag: featureFlagKey,
    sales_page_signup_framing_variant: routeVariant,
    [featureFlagContextProperty]: featureFlagVariant,
    [`$feature/${featureFlagKey}`]: featureFlagVariant,
    ...(featureFlagKey === 'sales_page_signup_framing_v4_variant' && {
      sales_page_signup_framing_v4_variant: featureFlagVariant,
    }),
    ...(featureFlagKey === 'sales_page_signup_framing_v5_variant' && {
      sales_page_signup_framing_v5_variant: featureFlagVariant,
      sales_page_signup_framing_v5_route_variant: routeVariant,
    }),
  };

  pushEvent({
    ...utmData,
    ...replayContext,
    event: '$feature_flag_called',
    $feature_flag: featureFlagKey,
    $feature_flag_response: featureFlagVariant,
    [`$feature/${featureFlagKey}`]: featureFlagVariant,
    $active_feature_flags: activeFeatureFlags,
    $enabled_feature_flags: enabledFeatureFlags,
    $feature_flag_id: featureFlagId,
    $feature_flag_version: featureFlagVersion,
    $feature_flag_reason: featureFlagReason,
    ...(posthogProps.$feature_flag_request_id && { $feature_flag_request_id: posthogProps.$feature_flag_request_id }),
    ...(posthogProps.$feature_flag_payloads && { $feature_flag_payloads: posthogProps.$feature_flag_payloads }),
    experiment_name: exposureContext.experiment_name,
    variant: featureFlagVariant,
    is_experiment_active: true,
    source: 'inline_checkout_contact_form',
    exposure_replayed_after_identify: true,
  });

  pushEvent({
    ...utmData,
    ...replayContext,
    event: 'experiment_exposure',
    experiment_name: exposureContext.experiment_name,
    variant: featureFlagVariant,
    feature_flag: featureFlagKey,
    feature_flag_value: featureFlagVariant,
    is_experiment_active: true,
    source: 'inline_checkout_contact_form',
    exposure_replayed_after_identify: true,
  });
};

const replaySignupFramingExposureForIdentifiedLead = ({ experimentContext, utmData }) => {
  replaySignupFramingExposure({
    exposureContext: experimentContext,
    parentExperimentContext: experimentContext,
    utmData,
  });
};

// Payment form component that uses Stripe hooks
export const PaymentFormWithStripe = ({ 
  initialData, 
  pricing, 
  clientSecret,
  onSuccess, 
  onError,
  productSlugs = ['starter-plan'],
  trialDays = 7,
  successUrl = '/signup-survey',
  smsConsentAccepted = false,
  onSmsConsentChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { refreshPaymentStatus } = usePaymentStatus();
  const { handleCustomJwtAuth } = useUnifiedAuth();
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [editableData, setEditableData] = useState({
    name: initialData.name || '',
    email: initialData.email || '',
    phone: initialData.phone || ''
  });

  const maybeConfirmInvoicePayment = React.useCallback(
    async (conversionResult) => {
      if (!conversionResult?.requiresAction || !conversionResult?.invoiceClientSecret) {
        return;
      }
      if (!stripe) {
        throw new Error('Payment processor is not ready to finalize your payment.');
      }

      const { error: invoiceError } = await confirmPaymentWithClient(stripe, null, {
        clientSecret: conversionResult.invoiceClientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      });

      if (invoiceError) {
        throw new Error(invoiceError.message || 'Additional payment confirmation failed. Please try again.');
      }
      refreshPaymentStatus({ forceRefresh: true });
    },
    [refreshPaymentStatus, stripe],
  );

  const handleContactFieldChange = (field, newValue) => {
    setEditableData((prev) => ({ ...prev, [field]: newValue }));
    if (field === 'phone') {
      onSmsConsentChange?.(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate terms acceptance
    if (!termsAccepted) {
      setError('⚠️ Please accept the Terms of Service to continue.');
      setTermsError('You must accept the Terms of Service to start your free trial');
      
      setTimeout(() => {
        const termsGroup = document.querySelector('.terms-checkbox-group');
        if (termsGroup) {
          termsGroup.classList.add('error');
          termsGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      return;
    }

    if (!stripe || !elements) {
      setError('Payment system is not ready. Please try again.');
      return;
    }

    setProcessing(true);
    setError('');
    setTermsError('');
    const smsConsentPayload = buildSmsConsentPayload({
      source: 'inline_checkout_payment_form',
      phoneNumber: editableData.phone,
      accepted: smsConsentAccepted,
    });

    try {
      // Confirm payment with Stripe
      const { error: stripeError, setupIntent, paymentIntent } = await confirmSetupWithClient(
        stripe,
        elements,
        {
          confirmParams: {
            return_url: `${window.location.origin}${successUrl}?from_starter_signup=true&email=${encodeURIComponent(editableData.email)}&source_flow=inline_checkout_success`,
          },
          redirect: 'if_required'
        }
      );

      if (stripeError) {
        console.error('❌ [InlineCheckout] Stripe error:', stripeError);
        setError(stripeError.message);
        setProcessing(false);
        
        if (onError) {
          onError(stripeError);
        }
        
        return;
      }

      // Check if payment method was successfully attached
      const hasValidPaymentMethod = (setupIntent?.payment_method && setupIntent.status === 'succeeded') || 
                                    (paymentIntent?.payment_method && paymentIntent.status === 'succeeded');

      if (!hasValidPaymentMethod) {
        console.error('❌ [InlineCheckout] Payment method validation failed');
        setError('Payment method validation failed. Please try again.');
        setProcessing(false);
        
        if (onError) {
          onError(new Error('Payment method validation failed'));
        }
        
        return;
      }

      console.log('✅ [InlineCheckout] Payment method successfully attached!');

      // 🎯 IMMEDIATE TRIAL CONVERSION: Call backend to convert lead to trial customer
      if (setupIntent && setupIntent.status === 'succeeded') {
        try {
          console.log('🔧 [InlineCheckout] Converting lead to trial customer immediately...');
          console.log('🔍 [InlineCheckout] Setup intent details:', {
            id: setupIntent.id,
            customer: setupIntent.customer,
            status: setupIntent.status
          });
          console.log('🔍 [InlineCheckout] Contact data:', editableData);
          const affiliateCode = getAffiliateCodeForApi();
          const affiliateFields = affiliateCode
            ? { affiliateCode, affiliate_code: affiliateCode, aff: affiliateCode }
            : {};
          console.info('[InlineCheckout] Affiliate conversion handoff', {
            affiliateCode: affiliateCode || null,
            hasAffiliateFields: Boolean(affiliateCode),
            email: editableData.email,
          });

          const conversionData = await convertLeadToTrial(
            {
              setupIntentId: setupIntent.id,
              setupIntentClientSecret: setupIntent.client_secret || clientSecret,
              customerId: setupIntent.customer,
              email: editableData.email,
              phoneNumber: editableData.phone,
              ...affiliateFields,
              ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {})
            },
            { apiUrl: API_URL }
          );

          await maybeConfirmInvoicePayment(conversionData);
          console.log('✅ [InlineCheckout] Lead successfully converted to trial customer:', conversionData);
          
          // Store customer info from conversion response for success callback
          window.starterPlanCustomerInfo = {
            customerId: conversionData.customerId,
            email: conversionData.email,
            paymentMethodId: setupIntent.payment_method,
            subscriptionId: conversionData.subscriptionId
          };
        } catch (conversionError) {
          console.error('❌ [InlineCheckout] Error converting lead to trial:', conversionError);
          setError(conversionError.message || 'Failed to activate your trial. Please try again.');
          setProcessing(false);
          if (onError) {
            onError(conversionError);
          }
          return;
        }
      }

      // Track successful payment
      const stripeId = setupIntent?.id || paymentIntent?.id || 'unknown';
      const utmData = getUtmParamsForApi();
      
      pushEvent({
        event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
        transaction_id: stripeId,
        value: 0, // Trial value is 0
        currency: 'USD',
        plan_type: productSlugs?.[0] || 'starter-plan',
        source: 'inline_checkout',
        email: editableData.email,
        name: editableData.name,
        phone: editableData.phone,
        ...utmData
      });

      try {
        refreshPaymentStatus({ forceRefresh: true });
      } catch (refreshError) {
        console.warn('[InlineCheckoutForm] Failed to refresh payment status after starter checkout:', refreshError);
      }

      // Success callback
      if (onSuccess) {
        // Check if we have customer info from the conversion response
        const customerInfo = window.starterPlanCustomerInfo;
        const enhancedResult = {
          setupIntent,
          paymentIntent,
          customerData: editableData,
          stripeId,
          // Add customer info if available
          ...(customerInfo && {
            customerInfo: {
              customerId: customerInfo.customerId,
              paymentMethodId: customerInfo.paymentMethodId || setupIntent?.payment_method,
              email: customerInfo.email
            }
          })
        };
        
        console.log('🔍 [InlineCheckout] Enhanced result for success callback:', enhancedResult);
        onSuccess(enhancedResult);
        
        // Clean up the temporary customer info
        delete window.starterPlanCustomerInfo;
      } else {
        // 🚨 AUTO-LOGIN FLOW: Call the auto-login endpoint immediately after successful payment
        const attemptAutoLogin = async () => {
          try {
            console.log('[InlineCheckoutForm] Attempting auto-login immediately after successful payment');
            
            // Get customer info from conversion response
            const customerInfo = window.starterPlanCustomerInfo;
            
            // Call the auto-login endpoint
            const autoLoginBody = {};
            if (paymentIntent?.id) {
              autoLoginBody.payment_intent_id = paymentIntent.id;
              autoLoginBody.payment_intent_client_secret = paymentIntent.client_secret || clientSecret;
            }
            if (setupIntent?.id) {
              autoLoginBody.setup_intent_id = setupIntent.id;
              autoLoginBody.setup_intent_client_secret = setupIntent.client_secret || clientSecret;
            }
            if (editableData.email) autoLoginBody.email = editableData.email;
            if (customerInfo?.customerId || setupIntent?.customer) {
              autoLoginBody.customer_id = customerInfo?.customerId || setupIntent?.customer;
            }
            
            // Construct the redirect URL to preserve current page and all query parameters
            const currentUrl = new URL(window.location.href);
            // Remove any payment-related params that we don't want to keep
            currentUrl.searchParams.delete('payment_intent');
            currentUrl.searchParams.delete('payment_intent_client_secret');
            currentUrl.searchParams.delete('redirect_status');
            currentUrl.searchParams.delete('setup_intent');
            currentUrl.searchParams.delete('setup_intent_client_secret');
            
            // Use the current path + remaining query params as redirectTo
            const redirectTo = currentUrl.pathname + currentUrl.search;
            autoLoginBody.redirectTo = redirectTo;
            
            console.log('[InlineCheckoutForm] Auto-login redirect URL constructed:', redirectTo);
            
            const response = await fetchWithTimeout(
              `${process.env.REACT_APP_API_URL}/stripe/autoLoginAfterPurchase`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(autoLoginBody)
              },
              AUTO_LOGIN_FETCH_TIMEOUT_MS,
            );
            
            if (!response.ok) {
              throw new Error(`Auto-login request failed: ${response.status}`);
            }
            
            const autoLoginData = await response.json();
            console.log('[InlineCheckoutForm] Auto-login response:', autoLoginData);
            
            if (autoLoginData.success) {
              if (await completeStripeAutoLogin(autoLoginData, {
                handleCustomJwtAuth,
                fallbackUrl: redirectTo,
                navigate,
              })) {
                return;
                
              } else if (autoLoginData.method === 'set_password' && autoLoginData.setPasswordUrl) {
                // Set password URL generated - redirect user to set their password
                console.log('[InlineCheckoutForm] Set password URL generated, redirecting to password setup');
                window.location.href = autoLoginData.setPasswordUrl;
                return;
                
              } else if (autoLoginData.method === 'email_instructions') {
                // User should check email for instructions
                console.log('[InlineCheckoutForm] Email instructions sent, showing user message');
                alert('Account created! Please check your email for login instructions.');
                fallbackToUpgrade(); // Continue to upgrade for now
                return;
              }
            }
            
            // Fallback: if auto-login fails, redirect to upgrade as before
            console.warn('[InlineCheckoutForm] Auto-login failed, falling back to upgrade redirect');
            fallbackToUpgrade();
            
          } catch (error) {
            console.error('[InlineCheckoutForm] Auto-login error:', error);
            // Fallback: if auto-login fails, redirect to upgrade as before
            fallbackToUpgrade();
          }
        };

        const fallbackToUpgrade = () => {
          // 🚨 UNIFIED FLOW FIX: Starter Plan purchases should go to Upgrade to Pro first (upsell), then survey
          // Include payment information for one-click upgrade
          console.log('🔍 [InlineCheckoutForm] Fallback to upgrade redirect');
          console.log('🔍 [InlineCheckoutForm] setupIntent:', setupIntent);
          console.log('🔍 [InlineCheckoutForm] editableData:', editableData);
          
          // Check if we have customer info from the conversion response
          const customerInfo = window.starterPlanCustomerInfo;
          console.log('🔍 [InlineCheckoutForm] customerInfo from conversion:', customerInfo);
          
          const params = new URLSearchParams();
          params.append('from_starter_signup', 'true');
          params.append('email', editableData.email);
          params.append('source_flow', 'inline_checkout_success');
          
          // Try to get customer info from conversion response first, then fallback to setupIntent
          const customerId = customerInfo?.customerId || setupIntent?.customer;
          const paymentMethodId = customerInfo?.paymentMethodId || setupIntent?.payment_method;
          
          if (customerId) {
            console.log('✅ [InlineCheckoutForm] Adding customerId:', customerId);
            params.append('customerId', customerId);
          } else {
            console.log('❌ [InlineCheckoutForm] No customer ID found');
          }
          
          if (paymentMethodId) {
            console.log('✅ [InlineCheckoutForm] Adding paymentMethodId:', paymentMethodId);
            params.append('paymentMethodId', paymentMethodId);
            
            // Try to get last4 from the payment method if available
            // Note: setupIntent might not have full payment method details, but we can try
            const last4 = setupIntent.payment_method_details?.card?.last4 || '****';
            params.append('last4', last4);
          } else {
            console.log('❌ [InlineCheckoutForm] No payment method ID found');
          }
          
          if (editableData.name) {
            params.append('name', editableData.name);
          }
          
          const redirectUrl = `/upgrade-to-pro?${params.toString()}`;
          console.log('🔗 [InlineCheckoutForm] Redirecting to:', redirectUrl);
          
          // Clean up the temporary customer info
          delete window.starterPlanCustomerInfo;
          
          navigate(redirectUrl);
        };

        // Attempt auto-login immediately after successful payment
        attemptAutoLogin();
      }

    } catch (err) {
      console.error('❌ [InlineCheckout] Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
      setProcessing(false);
      
      if (onError) {
        onError(err);
      }
    }
  };

  const formattedPrice =
    pricing?.formattedPrice || `$${pricing?.monthlyPrice || 99}/month`;

  return (
    <form onSubmit={handleSubmit} className="inline-checkout-form">
      {/* Contact Information */}
      <div className="contact-section-header">
        <h4>Your Information</h4>
      </div>
      <CheckoutContactFields
        value={editableData}
        onChange={handleContactFieldChange}
        disabled={processing}
        smsConsentId="inline-checkout-payment-sms-consent"
        smsConsentAccepted={smsConsentAccepted}
        onSmsConsentChange={onSmsConsentChange}
      />

      <CheckoutTrialCallout trialDays={trialDays} priceText={formattedPrice} guaranteeText="Easy cancellation" />


      {/* Stripe Payment Element */}
      <CheckoutPaymentContainer>
        <StripeErrorBoundary>
          <PaymentElement
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
              wallets: {
                applePay: process.env.NODE_ENV === 'production' ? 'auto' : 'never',
                googlePay: process.env.NODE_ENV === 'production' ? 'auto' : 'never',
              },
              fields: {
                billingDetails: {
                  name: 'auto',
                  email: 'auto',
                  phone: 'auto',
                  address: {
                    country: 'auto',
                    postalCode: 'auto',
                  },
                },
              },
            }}
            onLoadError={(paymentError) => {
              console.error('[InlineCheckoutForm] PaymentElement load error:', paymentError);
              setError('Payment form failed to load. Please refresh and try again.');
            }}
          />
        </StripeErrorBoundary>
      </CheckoutPaymentContainer>

      {/* Terms and Conditions */}
      <label className={`terms-checkbox-group ${termsError ? 'error' : ''}`}>
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) {
              setTermsError('');
              setError('');
              const termsGroup = document.querySelector('.terms-checkbox-group');
              if (termsGroup) {
                termsGroup.classList.remove('error');
              }
            }
          }}
          className="terms-checkbox-input"
          required
        />
        <span className="terms-text">
          I agree to the{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </a>
          {' '}and understand this will start my {trialDays}-day free trial
        </span>
      </label>
      {termsError && (
        <div className="terms-error-message">
          ❌ {termsError}
        </div>
      )}

      {error && (
        <div className="checkout-error-banner">
          {error}
        </div>
      )}

      <CheckoutCTAButton
        trialDays={trialDays}
        processing={processing}
        disabled={!stripe}
      />

      <div className="checkout-footer">
        <p className="trust-text">
          🔒 {trialDays}-day FREE trial • 30-day money-back guarantee • Easy refunds • Cancel anytime
        </p>
      </div>
    </form>
  );
};

// Main component that handles the complete flow
const InlineCheckoutForm = ({
  stripePromise,
  pricing,
  onSuccess,
  onError,
  productSlugs = ['starter-plan'],
  trialDays = 7,
  successUrl = '/signup-survey',
  autoScrollOnExpand = true,
  onContactSuccess = null,
  contactButtonLabel = '🚀 Continue →',
  showNameField = true,
  showPhoneField = true,
  emailPlaceholder = 'Your email address',
  namePlaceholder = 'Your full name',
  phonePlaceholder = 'Your phone number',
}) => {
  const [step, setStep] = useState('contact'); // 'contact' | 'attribution' | 'payment'
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [howFoundUs, setHowFoundUs] = useState('');
  const [howFoundUsOtherText, setHowFoundUsOtherText] = useState('');
  const [howFoundUsError, setHowFoundUsError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [customerId, setCustomerId] = useState(''); // Store customer ID from lead creation
  const [leadTransactionId, setLeadTransactionId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState({});
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);

  // Delay autofocus to prevent immediate scroll on page load
  useEffect(() => {
    // Wait a bit before enabling autofocus to avoid scrolling when user just arrived from homepage
    const timer = setTimeout(() => {
      setShouldAutoFocus(true);
      
      // Manually focus the name input without scrolling
      const nameInput = document.querySelector('input[name="name"]');
      if (nameInput && step === 'contact') {
        nameInput.focus({ preventScroll: true });
      }
    }, 1000); // Wait 1 second before enabling autofocus

    return () => clearTimeout(timer);
  }, [step]);

  // Auto-scroll when payment form appears and fire BEGIN_CHECKOUT event
  useEffect(() => {
    if (step === 'payment') {
      // Lead is emitted by Node after local user creation. This diagnostic is
      // intentionally separate and may fire only after that account request.
      setTimeout(() => {
        const tempUserId = 'temp_' + Date.now();
        const utmData = getUtmParamsForApi();
        const leadTransactionId = generateLeadTransactionId(contactData.email, 'lead_capture');
        
        // Get experiment context from global state
        const experimentContext = window.__experimentContext || {};
        
        pushEvent({
          event: ANALYTICS_EVENTS.BEGIN_CHECKOUT,
          transaction_id: leadTransactionId, // Use same transaction ID for funnel tracking
          source: 'inline_checkout',
          userId: tempUserId,
          user_type: 'guest',
          payment_type: clientSecret?.includes('seti_') ? 'stripe_setup_intent' : 'stripe_elements',
          plan_type: productSlugs[0] || 'starter-plan',
          email: contactData.email,
          name: contactData.name,
          phone: contactData.phone,
          checkout_method: 'inline_form_transition',
          value: pricing?.basePrice?.amount ? pricing.basePrice.amount / 100 : 99,
          currency: 'USD',
          ...utmData,
          ...experimentContext // Include experiment variant information
        });
      }, 100);

      if (autoScrollOnExpand) {
        setTimeout(() => {
          const paymentSection = document.querySelector('.payment-section-header');
          if (paymentSection) {
            paymentSection.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest'
            });
          }
        }, 300);
      }
    }
  }, [step, autoScrollOnExpand, contactData.email, contactData.name, contactData.phone, clientSecret, pricing, productSlugs]);

  const validateContactForm = () => {
    const newErrors = {};
    
    if (showNameField && !contactData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!contactData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (showPhoneField) {
      if (!contactData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^[\d\s+()-]+$/.test(contactData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData(prev => ({ ...prev, [name]: value }));
    if (name === 'phone' && smsConsentAccepted) {
      setSmsConsentAccepted(false);
    }
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'phone' && errors.smsConsent) {
      setErrors(prev => ({ ...prev, smsConsent: '' }));
    }
  };

  const handleSmsConsentChange = (checked) => {
    setSmsConsentAccepted(checked);
    if (checked && errors.smsConsent) {
      setErrors(prev => ({ ...prev, smsConsent: '' }));
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();

    if (!validateContactForm()) return;
    blurActiveFormControl();

    const utmData = getUtmParamsForApi();
    const experimentContext = window.__experimentContext || {};

    // 🎯 CRITICAL: PostHog User Aliasing - Merge anonymous user with identified user
    identifyUserFromLeadCapture(contactData.email, {
      name: contactData.name,
      phone: contactData.phone,
      lead_type: 'checkout_contact_form',
      form_step: 'contact_submission'
    }, 'inline_checkout_contact_form');

    replaySignupFramingExposureForIdentifiedLead({ experimentContext, utmData });

    // Keep the transaction correlation id, but do not emit Lead here. The
    // protected Lead contract is server-owned and fires only after a new local
    // User has been saved.
    const newLeadTransactionId = generateLeadTransactionId(contactData.email, 'lead_capture');

    setLeadTransactionId(newLeadTransactionId);

    // Lead notification moved to handleAttributionSubmit (step 2) so it
    // includes the "How did you find us" dropdown answer.
    // Advance to attribution step.
    setStep('attribution');
  };

  const handleAttributionSubmit = async (e) => {
    e.preventDefault();

    if (!howFoundUs) {
      setHowFoundUsError('Please tell us how you found BotSpot');
      return;
    }
    blurActiveFormControl();

    const utmData = getUtmParamsForApi();
    const affiliateCode = getAffiliateCodeForApi();
    const affiliateFields = affiliateCode
      ? { affiliateCode, affiliate_code: affiliateCode, aff: affiliateCode }
      : {};
    console.info('[InlineCheckout] Affiliate payload handoff', {
      affiliateCode: affiliateCode || null,
      hasAffiliateFields: Boolean(affiliateCode),
      email: contactData.email,
      productSlugs,
    });
    const experimentContext = window.__experimentContext || {};
    const effectiveLeadTransactionId = leadTransactionId || generateLeadTransactionId(contactData.email, 'lead_capture');
    const checkoutAttemptId = createCheckoutAttemptId('inline_checkout');
    const smsConsentPayloadForRequest = buildSmsConsentPayload({
      source: 'inline_checkout_contact_form',
      phoneNumber: contactData.phone,
      accepted: smsConsentAccepted,
    });
    const leadRequestBody = {
      productSlugs,
      email: contactData.email,
      customerName: contactData.name,
      phoneNumber: contactData.phone,
      ...(smsConsentPayloadForRequest ? { smsConsent: smsConsentPayloadForRequest } : {}),
      howFoundUs,
      ...(howFoundUsOtherText && { howFoundUsOtherText }),
      trialDays,
      requirePaymentIntent: true,
      checkoutAttemptId,
      leadTransactionId: effectiveLeadTransactionId,
      ...utmData,
      ...affiliateFields,
      ...experimentContext,
      terms: {
        docType: TERMS_DOC_TYPE,
        version: TERMS_VERSION,
        acceptedAt: new Date().toISOString(),
        hash: null
      }
    };
    const leadNotificationBody = {
      email: contactData.email,
      firstName: contactData.name?.split(' ')[0] || '',
      lastName: contactData.name?.split(' ').slice(1).join(' ') || '',
      phone: contactData.phone || '',
      ...(smsConsentPayloadForRequest ? { smsConsent: smsConsentPayloadForRequest } : {}),
      howFoundUs: howFoundUs || '',
      howFoundUsOtherText: howFoundUsOtherText || '',
      howFoundUsDetails: howFoundUsOtherText || '',
      ...utmData,
      salesPage: window.location.pathname,
      abTestVariant: window.__experimentContext?.experiment_variant || '',
      experimentName: window.__experimentContext?.experiment_name || '',
      referrer: document.referrer || '',
    };

    if (typeof onContactSuccess === 'function') {
      if (!leadTransactionId) {
        setLeadTransactionId(effectiveLeadTransactionId);
      }

      setIsSubmitting(true);
      setApiError('');
      setHowFoundUsError('');

      try {
        const response = await fetchWithTimeout(`${API_URL}/stripe/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Request-Id': checkoutAttemptId },
          body: JSON.stringify(leadRequestBody),
        }, PAYMENT_INTENT_FETCH_TIMEOUT_MS);

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.message || 'Failed to initialize checkout');
        }
        if (data.existingUser === true) {
          console.info('[InlineCheckout] Existing user detected after sales handoff; keeping fast sales flow.');
        } else if (!data.autoLoginToken) {
          console.warn('[InlineCheckout] Lead handoff did not include an auto-login token; continuing without seamless login.');
        }

        if (data.customerType === 'lead') {
          trackGoogleAdsLeadConversion({
            transactionId: data.leadTransactionId || effectiveLeadTransactionId,
          });
        }

        fetch(`${API_URL}/users/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadNotificationBody),
          keepalive: true,
        }).catch((error) => {
          console.warn('[InlineCheckout] Background contact notification failed:', error);
        });

        onContactSuccess({
          contactData,
          smsConsent: smsConsentPayloadForRequest,
          howFoundUs,
          howFoundUsOtherText: howFoundUsOtherText || null,
          leadTransactionId: effectiveLeadTransactionId,
          utm: utmData,
          experimentContext,
          customerId: data.customerId || null,
          customerType: data.customerType || 'lead',
          autoLoginToken: data.autoLoginToken || null,
          existingUser: data.existingUser === true,
          redirectTo: data.redirectTo || null,
        });
      } catch (error) {
        console.error('[InlineCheckout] Lead handoff setup failed:', error);
        const message = error.message || 'We could not finish setting up your account. Please try again.';
        setApiError(message);
        onError?.(message);
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    setIsSubmitting(true);
    setApiError('');
    setHowFoundUsError('');

    try {
      // Create payment intent with attribution data included
      const response = await fetchWithTimeout(`${API_URL}/stripe/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Request-Id': checkoutAttemptId },
        body: JSON.stringify(leadRequestBody)
      }, PAYMENT_INTENT_FETCH_TIMEOUT_MS);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to initialize checkout');
      }

      const checkoutResponse = classifyCheckoutResponse(data);
      if (checkoutResponse.kind === 'completed_subscription') {
        onSuccess?.({
          checkoutComplete: true,
          response: data,
          subscriptionId: checkoutResponse.subscriptionId,
          customerId: checkoutResponse.customerId,
          collectedAmountCents: 0,
        });
        return;
      }

      // 🔐 EXISTING PAYING CUSTOMER CHECK: Redirect to login if they already have an account
      if (data.existingUser === true) {
        console.log('🔒 [InlineCheckout] Existing paying customer detected, redirecting to login');
        const redirectTo = data.redirectTo || '/login?returnTo=/ai-agent';
        window.location.href = redirectTo;
        return;
      }

      // ---- Notify backend of new lead (team notification + Odoo sync) ----
      // Fires at step 2 after the user answers "How did you find us?" so the
      // email includes all 5 required fields: howFoundUs + 4 UTMs.
      try {
        fetch(`${API_URL}/users/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadNotificationBody)
        }).catch(err => console.warn('[InlineCheckout] Lead notification failed:', err));
      } catch (err) {
        console.warn('[InlineCheckout] Lead notification error:', err);
      }

      // If onContactSuccess callback is provided, call it with lead + attribution data and return.
      // This allows sales pages to redirect after lead creation without proceeding to payment.
      if (typeof onContactSuccess === 'function') {
        console.log('✅ [InlineCheckout] Lead created, calling onContactSuccess callback:', data);
        onContactSuccess({
          contactData,
          smsConsent: smsConsentPayloadForRequest,
          howFoundUs,
          howFoundUsOtherText: howFoundUsOtherText || null,
          leadTransactionId,
          utm: utmData,
          experimentContext,
          customerId: data.customerId,
          customerType: data.customerType,
          // 🔐 LEAD AUTO-LOGIN TOKEN: Include the JWT token for seamless authentication
          autoLoginToken: data.autoLoginToken || null,
        });
        setIsSubmitting(false);
        return;
      }

      // Check if we got a real clientSecret or a lead response
      if (
        checkoutResponse.kind === 'requires_confirmation'
        && !checkoutResponse.clientSecret.includes('1234567890')
      ) {
        // Real clientSecret - proceed to payment form
        console.log('✅ [InlineCheckout] Real payment intent created:', data.clientSecret);
        setClientSecret(checkoutResponse.clientSecret);
        setCustomerId(data.customerId);
        setStep('payment');
      } else if (data.customerType === 'lead' && data.customerId) {
        // Lead created - now create a setup intent for payment collection
        console.log('✅ [InlineCheckout] Lead created, now creating setup intent for payment collection:', data);

        const setupResponse = await fetchWithTimeout(`${API_URL}/stripe/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Request-Id': checkoutAttemptId },
          body: JSON.stringify({
            productSlugs,
            email: contactData.email,
            customerName: contactData.name,
            phoneNumber: contactData.phone,
            ...(smsConsentPayloadForRequest ? { smsConsent: smsConsentPayloadForRequest } : {}),
            customerId: data.customerId,
            howFoundUs,
            ...(howFoundUsOtherText && { howFoundUsOtherText }),
            trialDays,
            requirePaymentIntent: true,
            forceSubscriptionCreation: true,
            checkoutAttemptId,
            leadTransactionId: effectiveLeadTransactionId,
            ...utmData,
            ...affiliateFields,
            terms: {
              docType: TERMS_DOC_TYPE,
              version: TERMS_VERSION,
              acceptedAt: new Date().toISOString(),
              hash: null
            }
          })
        }, PAYMENT_INTENT_FETCH_TIMEOUT_MS);

        const setupData = await setupResponse.json();

        const setupCheckoutResponse = classifyCheckoutResponse(setupData);
        if (setupResponse.ok && setupCheckoutResponse.kind === 'completed_subscription') {
          onSuccess?.({
            checkoutComplete: true,
            response: setupData,
            subscriptionId: setupCheckoutResponse.subscriptionId,
            customerId: setupCheckoutResponse.customerId,
            collectedAmountCents: 0,
          });
          return;
        }
        if (setupResponse.ok && setupCheckoutResponse.kind === 'requires_confirmation') {
          console.log('✅ [InlineCheckout] Setup intent for payment collection created:', setupData.clientSecret);
          setClientSecret(setupCheckoutResponse.clientSecret);
          setCustomerId(data.customerId);
          setStep('payment');
        } else {
          console.error('❌ [InlineCheckout] Failed to create setup intent:', setupData);
          throw new Error(setupData.message || 'Failed to create payment form');
        }
      } else {
        throw new Error('Failed to create payment form');
      }

    } catch (err) {
      console.error('❌ [InlineCheckout] Attribution form error:', err);
      setApiError(err.message || 'Something went wrong. Please try again.');

      if (onError) {
        onError(err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show contact form first
  if (step === 'contact') {
    return (
      <div className="inline-checkout-container">
        <form onSubmit={handleContactSubmit} className="contact-form" autoComplete="on">
          {showNameField && (
            <div className="form-group">
                <input
                  id="inline-checkout-name"
                  type="text"
                  name="name"
                  placeholder={namePlaceholder}
                  aria-label={namePlaceholder}
                  value={contactData.name}
                  onChange={handleContactChange}
                  className={`checkout-input ${errors.name ? 'error' : ''}`}
                  autoComplete="name"
                  autoFocus={shouldAutoFocus}
                  data-hj-allow
                />
                {errors.name && (
                  <div className="error-text">{errors.name}</div>
                )}
            </div>
          )}

          <div className="form-group">
            <input
              id="inline-checkout-email"
              type="email"
              name="email"
              placeholder={emailPlaceholder}
              aria-label={emailPlaceholder}
              value={contactData.email}
              onChange={handleContactChange}
              className={`checkout-input ${errors.email ? 'error' : ''}`}
              autoComplete="email"
              inputMode="email"
              data-hj-allow
            />
            {errors.email && (
              <div className="error-text">{errors.email}</div>
            )}
          </div>

          {showPhoneField && (
            <>
              <div className="form-group">
                <input
                  id="inline-checkout-phone"
                  type="tel"
                  name="phone"
                  placeholder={phonePlaceholder}
                  aria-label={phonePlaceholder}
                  value={contactData.phone}
                  onChange={handleContactChange}
                  className={`checkout-input ${errors.phone ? 'error' : ''}`}
                  autoComplete="tel"
                  data-hj-allow
                />
                {errors.phone && (
                  <div className="error-text">{errors.phone}</div>
                )}
              </div>
              <SmsConsentCheckbox
                id="inline-checkout-sms-consent"
                checked={smsConsentAccepted}
                onChange={handleSmsConsentChange}
                error={errors.smsConsent}
              />
            </>
          )}

          {apiError && (
            <div className="api-error">
              {apiError}
            </div>
          )}

          <SparkleButton
            type="submit"
            disabled={isSubmitting}
            className="sparkle-button-cta"
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                ✨ Creating Your Account...
              </>
            ) : (
              contactButtonLabel
            )}
          </SparkleButton>

          <div className="form-footer">
            <p className="trust-text">
              🔒 Your information is secure and will never be shared
            </p>
          </div>
        </form>
      </div>
    );
  }

  // Show attribution step: ask "How did you find us?" before showing payment
  if (step === 'attribution') {
    return (
      <div className="inline-checkout-container">
        <form onSubmit={handleAttributionSubmit} className="inline-checkout-form">
          <div className="inline-checkout-header">
            <h3 className="inline-checkout-title">One quick question...</h3>
            <p className="inline-checkout-subtitle">How did you hear about BotSpot?</p>
          </div>

          <HowFoundUsDropdown
            value={howFoundUs}
            otherText={howFoundUsOtherText}
            onChange={(val) => {
              setHowFoundUs(val);
              if (howFoundUsError) setHowFoundUsError('');
            }}
            onOtherTextChange={(val) => setHowFoundUsOtherText(val)}
            disabled={isSubmitting}
          />

          {howFoundUsError && (
            <div className="error-text">{howFoundUsError}</div>
          )}

          {apiError && (
            <div className="api-error">{apiError}</div>
          )}

          <SparkleButton
            type="submit"
            disabled={isSubmitting}
            className="sparkle-button-cta"
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                ✨ Setting up your account...
              </>
            ) : (
              '🚀 Continue →'
            )}
          </SparkleButton>

          <div className="form-footer">
            <p className="trust-text">
              🔒 Your information is secure and will never be shared
            </p>
          </div>
        </form>
      </div>
    );
  }

  // Show payment form with Stripe Elements
  if (step === 'payment' && clientSecret && stripePromise) {
    return (
      <div className="inline-checkout-container expanded">
        <div className="checkout-celebration-header">
          <h2 className="checkout-title">🎉 Almost There! Complete Your Registration</h2>
          <p className="checkout-subtitle">
            Start your <strong>{trialDays}-day FREE trial</strong>, then just <strong>${pricing?.monthlyPrice || 99}/month</strong>
          </p>
        </div>

        <Elements 
          stripe={stripePromise} 
          options={{
            clientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#ff6b35',
                colorBackground: '#1a1a1a',
                colorText: '#ffffff',
                colorDanger: '#ff6b6b',
                fontFamily: 'system-ui, sans-serif',
                spacingUnit: '2px',
                borderRadius: '16px'
              },
              rules: {
                '.Tab': {
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '8px',
                  color: '#ffffff'
                },
                '.Tab--selected': {
                  border: '1px solid #ff6b35',
                  backgroundColor: 'rgba(255, 107, 53, 0.15)',
                  color: '#ffffff'
                },
                '.Input': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '16px',
                  padding: '1.2rem 1.5rem',
                  fontSize: '1.05rem',
                  color: '#ffffff',
                  marginBottom: '1rem'
                },
                '.Input:focus': {
                  borderColor: 'rgba(255, 107, 53, 0.6)',
                  boxShadow: '0 0 0 3px rgba(255, 107, 53, 0.2)'
                }
              }
            }
          }}
        >
          <PaymentFormWithStripe 
            initialData={contactData}
            pricing={pricing}
            clientSecret={clientSecret}
            onSuccess={onSuccess}
            onError={onError}
            productSlugs={productSlugs}
            trialDays={trialDays}
            successUrl={successUrl}
            customerId={customerId}
            smsConsentAccepted={smsConsentAccepted}
            onSmsConsentChange={handleSmsConsentChange}
          />
        </Elements>
      </div>
    );
  }

  // Loading state
  return (
    <div className="inline-checkout-container">
      <div className="checkout-loading">
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading payment form...</span>
      </div>
    </div>
  );
};

export default InlineCheckoutForm;
